<script setup lang="ts">
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import type { ReportInput } from "../services/types";
import type { ReportPhase } from "../composables/useReportFlow";
import type { AppError } from "../services/errors";
import EmailSummary from "./EmailSummary.vue";
import ErrorNotice from "./ErrorNotice.vue";
import AppIcon from "./AppIcon.vue";
import LoadingSpinner from "./LoadingSpinner.vue";

const props = defineProps<{
  subject: string;
  sender: string;
  phase: ReportPhase;
  error: AppError | null;
}>();
const emit = defineEmits<{ submit: [input: ReportInput] }>();
const { t } = useI18n();
const form = reactive<ReportInput>({
  category: "phishing",
  details: { clicked_link: false, opened_attachment: false, comment: "" },
});
const busy = computed(() => props.phase === "reading" || props.phase === "submitting");
const disabled = computed(() => busy.value || !!(props.error && !props.error.retryable));
const buttonLabel = computed(() => {
  if (props.phase === "reading") return t("reading_email");
  if (props.phase === "submitting") return t("submitting_report");
  if (props.error?.messageKey === "error_auth") return t("login");
  return t(form.category === "spam" ? "report_spam" : "report_phishing");
});

function submit() {
  if (disabled.value) return;
  emit("submit", { category: form.category, details: { ...form.details } });
}
</script>

<template>
  <form class="card" :aria-busy="busy" @submit.prevent="submit">
    <h1 class="mb-4 text-xl font-bold">{{ t("report_this_email") }}</h1>
    <EmailSummary :subject="subject" :sender="sender" />
    <fieldset class="mt-4 min-w-0 border-t border-slate-200 pt-4" :disabled="disabled">
      <legend class="sr-only">{{ t("report_as") }}</legend>
      <fieldset>
        <legend class="text-sm font-medium text-slate-500">{{ t("report_as") }}</legend>
        <div
          class="mt-3 grid grid-cols-2 gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1"
        >
          <label
            v-for="category in ['phishing', 'spam'] as const"
            :key="category"
            class="relative cursor-pointer"
          >
            <input
              v-model="form.category"
              class="peer sr-only"
              type="radio"
              name="reportCategory"
              :value="category"
            />
            <span
              class="flex min-h-16 flex-col items-center justify-center gap-1 rounded-md text-sm font-semibold text-slate-500 transition peer-checked:bg-white peer-checked:text-slate-950 peer-checked:shadow-xs peer-focus-visible:ring-2 peer-focus-visible:ring-slate-500"
            >
              <AppIcon :name="category" class="size-5" />
              {{ t(category) }}
            </span>
          </label>
        </div>
      </fieldset>
      <fieldset class="mt-6">
        <legend class="text-sm font-medium text-slate-500">{{ t("did_interact") }}</legend>
        <div class="mt-3 flex flex-col gap-3">
          <label class="interaction-option">
            <input
              v-model="form.details.clicked_link"
              type="checkbox"
              class="size-4 accent-slate-950"
            />
            <AppIcon name="link" class="size-5 shrink-0 text-slate-500" />
            {{ t("clicked_link") }}
          </label>
          <label class="interaction-option">
            <input
              v-model="form.details.opened_attachment"
              type="checkbox"
              class="size-4 accent-slate-950"
            />
            <AppIcon name="attachment" class="size-5 shrink-0 text-slate-500" />
            {{ t("opened_attachment") }}
          </label>
        </div>
      </fieldset>
      <label for="report-comment" class="mt-6 block text-sm font-medium text-slate-500">{{
        t("comment_optional")
      }}</label>
      <textarea
        id="report-comment"
        v-model="form.details.comment"
        class="mt-3 min-h-24 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
        :placeholder="t('comment_placeholder')"
      />
      <button type="submit" class="primary-button mt-4 w-full" :disabled="disabled">
        <LoadingSpinner v-if="busy" class="size-5" />
        <AppIcon v-else name="send" class="size-5" />
        <span aria-live="polite">{{ buttonLabel }}</span>
      </button>
    </fieldset>
    <ErrorNotice v-if="error" class="mt-4" :error="error" />
  </form>
</template>
